import { Account, AccountIntegrationConfig, MeetingProviderAccountIntegrationConfig } from '../models/Account'
import { Me } from '../models/User'
import { IntegrationServiceId } from './integrations'

/** Meeting providers that require the user to have a connected (live) integration. */
export const INTEGRATIONS_REQUIRING_USER_CONNECTION = new Set([IntegrationServiceId.ZOOM])

export interface IntegrationRequirementResult {
  satisfied: boolean
  accountAllowed: boolean
}

const findZoomMeetingProviderConfig = (integrations: AccountIntegrationConfig[] | undefined): MeetingProviderAccountIntegrationConfig | undefined =>
  integrations?.find((i): i is MeetingProviderAccountIntegrationConfig => i.type === 'meeting_provider' && i.serviceId === IntegrationServiceId.ZOOM)

/**
 * Is the enterprise locking the Zoom strict-join (companion mode) setting at any value?
 * When true, the user-facing toggle should be disabled and an enforcement banner shown.
 */
export const isZoomStrictJoinEnforced = (account: Account | undefined): boolean =>
  findZoomMeetingProviderConfig(account?.enterpriseConfig?.integrations)?.strictJoinEnabled != null

export const getEnforcedZoomMeetingCaptureType = (account: Account | undefined): 'bot' | 'recording' | null =>
  findZoomMeetingProviderConfig(account?.enterpriseConfig?.integrations)?.meetingCaptureType ?? null

/**
 * Is the binding Zoom strict-join (companion mode) setting `true`?
 *
 * Walks the binding chain — enterprise enforcement first, then the user-level override.
 * The account-level default is intentionally **not** considered: it's a default for new users,
 * not a directive, so users without an integration can still use Zoom via the service-user ZAK fallback.
 *
 * When true, the bot must use the OBF token, which requires the user to have a live Zoom integration.
 */
export const isZoomStrictJoinRequired = (user: Me | undefined, account: Account | undefined): boolean => {
  const enterpriseValue = findZoomMeetingProviderConfig(account?.enterpriseConfig?.integrations)?.strictJoinEnabled
  if (enterpriseValue != null) return enterpriseValue === true

  const zoomIntegration = user?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ZOOM)
  if (zoomIntegration?.configuration?.type === 'bot' && zoomIntegration.configuration.strictJoinEnabled != null) {
    return zoomIntegration.configuration.strictJoinEnabled === true
  }

  return false
}

/**
 * Resolves the effective Zoom strict-join (companion mode) setting for the current user.
 *
 * Order:
 *   1. account `enterpriseConfig.integrations[zoom].strictJoinEnabled` (non-null wins — enterprise enforcement)
 *   2. user-integration `strictJoinEnabled` (non-null wins — user override)
 *   3. account `config.integrations[zoom].strictJoinEnabled` (non-null wins — account default)
 *   4. default `false`
 */
export const resolveZoomStrictJoinEnabled = (user: Me | undefined, account: Account | undefined): boolean => {
  const enterpriseEntry = findZoomMeetingProviderConfig(account?.enterpriseConfig?.integrations)
  if (enterpriseEntry?.strictJoinEnabled != null) return enterpriseEntry.strictJoinEnabled

  const zoomIntegration = user?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ZOOM)
  if (zoomIntegration?.configuration?.type === 'bot' && zoomIntegration.configuration.strictJoinEnabled != null) {
    return zoomIntegration.configuration.strictJoinEnabled
  }

  const accountEntry = findZoomMeetingProviderConfig(account?.config?.integrations)
  if (accountEntry?.strictJoinEnabled != null) return accountEntry.strictJoinEnabled

  return false
}
